import React, { useMemo, useState } from 'react';
import { useButtonStyles } from './hooks/useButtonStyles.js';
import { useFieldAttrs } from './hooks/useFieldAttrs.js';
import { useFieldStyles } from './hooks/useFieldStyles.js';
import { FieldWrap } from './FieldWrap.js';
import { FieldLabel } from './FieldLabel.js';

function getCheckedCss(theme) {
    if (theme === 'light') {
        return 'border-white';
    }
    return 'border-lilac-300 secondary:!border-indigo-500';
}
function getTypeCss(checked, type, theme) {
    const styles = ['bg-contain block cursor-pointer'];
    if (checked) {
        styles.push(theme === 'light'
            ? 'bg-check'
            : '!bg-lilac-300 bg-check secondary:bg-indigo-500');
        styles.push(getCheckedCss(theme));
    }
    if (type === 'radio') {
        styles.push('!rounded-full');
    }
    return styles.join(' ');
}
function getValueType(fieldValue) {
    switch (true) {
        case Array.isArray(fieldValue):
            return 'array';
        default:
            return typeof fieldValue;
    }
}
function isInputChecked({ fieldValue, hasOptions, inputValue, }) {
    const fieldValueType = getValueType(fieldValue);
    switch (true) {
        case fieldValueType === 'array':
            return fieldValue.includes(inputValue);
        case hasOptions:
            return fieldValue === inputValue;
        default:
            return fieldValue;
    }
}
function setFieldValue({ fieldValue, hasOptions, inputValue, checked, }) {
    const fieldValueType = getValueType(fieldValue);
    switch (true) {
        case fieldValueType === 'array':
            return checked
                ? fieldValue.concat([inputValue])
                : fieldValue.filter(optionValue => {
                    return optionValue !== inputValue;
                });
        case hasOptions:
            return inputValue;
        default:
            return fieldValueType === 'boolean' ? checked : fieldValue;
    }
}
const Toggle = React.forwardRef(({ checked, children, className = '', groupId, invalid, onChange, theme, type = 'checkbox', variant = 'default', ...props }, ref) => {
    const buttonStyles = useButtonStyles({
        filled: checked,
        rounded: true,
        variant: 'secondary',
    });
    const inputStyles = useFieldStyles({
        invalid,
        theme,
        type,
        ...props,
    });
    const defaultWrapStyles = useFieldStyles({
        invalid,
        theme,
        ...props,
    });
    const controlStyles = useMemo(() => {
        const currentStyles = getTypeCss(checked, type, theme);
        return `${inputStyles} ${currentStyles}`;
    }, [inputStyles, type, checked, theme]);
    const wrapStyles = useMemo(() => {
        const wrapGrid = 'grid gap-x-2 grid-cols-2-auto content-start';
        if (variant === 'default') {
            return wrapGrid;
        }
        if (variant === 'button') {
            return buttonStyles;
        }
        const checkedStyles = getCheckedCss(theme);
        return [
            wrapGrid,
            defaultWrapStyles.replace('bg-white', ''),
            checked ? checkedStyles : '',
        ].join(' ');
    }, [buttonStyles, checked, defaultWrapStyles, variant, theme]);
    return (React.createElement("div", { ...{
            className: ['relative', wrapStyles, className].join(' '),
            ...(groupId ? { id: groupId } : {}),
        } },
        onChange && (React.createElement("input", { className: "absolute w-full h-full opacity-0 left-0 top-0 cursor-pointer", ...{
                ...props,
                checked,
                invalid: invalid?.toString(),
                onChange,
                ref,
                type,
            } })),
        React.createElement("span", { "aria-hidden": "true", className: [
                controlStyles,
                variant === 'button' ? 'hidden' : 'w-5 h-5 p-0',
            ].join(' ') }),
        children));
});
const ToggleInput = React.forwardRef(({ hasOptions = false, id: groupId, inputValue = null, invalid, label, name, onChange, variant = 'button', value, type = 'checkbox', ...props }, ref) => {
    const fieldValue = useMemo(() => {
        if (!value && hasOptions && type === 'checkbox') {
            return [];
        }
        return value;
    }, [hasOptions, value, type]);
    const [checked, setChecked] = useState(isInputChecked({ fieldValue, hasOptions, inputValue }));
    const { id } = useFieldAttrs({ invalid, name });
    useMemo(() => {
        setChecked(isInputChecked({ fieldValue, hasOptions, inputValue }));
    }, [fieldValue, hasOptions, inputValue]);
    return (React.createElement(Toggle, { ...{
            ...props,
            checked,
            groupId,
            id,
            invalid,
            name,
            onChange: e => {
                setChecked(e.target.checked);
                onChange({
                    ...e,
                    target: {
                        ...e.target,
                        value: setFieldValue({
                            fieldValue,
                            hasOptions,
                            inputValue,
                            checked: e.target.checked,
                        }),
                    },
                });
            },
            ref,
            type,
            value: inputValue || fieldValue,
            variant,
        } },
        React.createElement(FieldLabel, { id: id, label: label, size: "small", labelVisible: true })));
});
const ToggleField = React.forwardRef(({ label, options, variant = 'default', theme = 'dark', type, ...props }, ref) => {
    const hasOptions = options && options.length > 0;
    const inputProps = {
        ref,
        variant,
        theme,
        type,
        ...props,
    };
    return hasOptions ? (React.createElement(React.Fragment, null, options.map(({ label, value }, key) => (React.createElement(ToggleInput, { ...{
            ...inputProps,
            key,
            label,
            hasOptions,
            inputValue: value,
        } }))))) : (
    // @ts-ignore: missing props are added via React.cloneElement in FieldWrap
    React.createElement(ToggleInput, { ...{
            label,
            ...inputProps,
        } }));
});
const ToggleFieldControlled = ({ className, label, options, variant = 'default', theme, type, ...props }) => {
    const hasOptions = options && options.length > 0;
    return (React.createElement(FieldWrap, { ...{
            label: hasOptions ? label : null,
            theme,
            ...props,
        } },
        React.createElement(ToggleField, { ...{
                className,
                label,
                variant,
                theme,
                options,
                type,
            } })));
};

export { Toggle, ToggleField, ToggleFieldControlled };
//# sourceMappingURL=ToggleField.js.map
